import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Box, IconButton, MenuItem, Select, Snackbar, Tooltip, Typography } from "@mui/material";
import HelpIcon from "@mui/icons-material/HelpOutline";
import ConfirmationDialog from "../utils/confirmationDialog";
import { useGetTenancyByIdQuery, useUpdateSyncFilteringMutation } from "../store/services/tenancy.service";

const ACTIONS = ["Unchanged", "Disable", "Remove"];

const ACTION_LABELS = {
    Unchanged: "No (unchanged)",
    Disable: "Yes (disable)",
    Remove: "Yes (remove)"
};

// The tenancy GET serialises the enum as its number, the update response returns
// its name, and tenancies created before the feature only have the legacy bool.
export const normalizeSyncInactiveUserAction = (tenancy) => {
    const value = tenancy?.syncInactiveUserAction;
    if (typeof value === "number" && ACTIONS[value]) return ACTIONS[value];
    if (ACTIONS.includes(value)) return value;
    return tenancy?.enableSyncFiltering ? "Remove" : "Unchanged";
};

const CONFIRMATIONS = {
    Disable: {
        title: "Disable inactive directory users?",
        content: (
            <>
                Users whose accounts are disabled in Microsoft Entra will be disabled here during the next sync.
                They will no longer be able to log in, but their history is kept.
                <br />
                <br />
                This change will take effect on your next directory sync.
            </>
        )
    },
    Remove: {
        title: "Remove inactive directory users?",
        content: (
            <>
                Users whose accounts are inactive in the connected directory will be removed during the next sync.
                <br />
                <br />
                • <strong>Microsoft Entra:</strong> Removes users where accountEnabled = false.
                <br />
                • <strong>Google Workspace:</strong> Removes users where archived = true and disables users where suspended = true.
                <br />
                <br />
                This change will take effect on your next directory sync.
            </>
        )
    }
};

/**
 * Dropdown for the tenancy's "Apply account status rules?" setting, deciding what
 * sync does with users who are inactive at the identity provider: leave them
 * unchanged, disable them (Microsoft only), or remove them.
 */
export default function SyncInactiveUserActionControl({ tenancyId, isGoogle = false }) {
    const { data: tenancy } = useGetTenancyByIdQuery({ tenancyId }, { skip: !tenancyId });
    const [updateSyncFiltering, { isLoading }] = useUpdateSyncFilteringMutation();

    const [action, setAction] = useState("Unchanged");
    const [pendingAction, setPendingAction] = useState(null);
    const [snackbar, setSnackbar] = useState(null);

    useEffect(() => {
        if (tenancy) {
            setAction(normalizeSyncInactiveUserAction(tenancy));
        }
    }, [tenancy]);

    const applyAction = (newAction) => {
        updateSyncFiltering({ tenancyId, syncInactiveUserAction: newAction })
            .unwrap()
            .then(() => {
                setAction(newAction);
                setSnackbar({ severity: "success", message: `Account status rules set to "${ACTION_LABELS[newAction]}"` });
            })
            .catch((error) => {
                console.error("Error updating account status rules:", error);
                setSnackbar({ severity: "error", message: "Failed to update account status rules" });
            });
    };

    const handleChange = (event) => {
        const newAction = event.target.value;
        if (newAction === action) return;
        if (CONFIRMATIONS[newAction]) {
            setPendingAction(newAction);
        } else {
            applyAction(newAction);
        }
    };

    return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body2" sx={{ mr: 1, whiteSpace: "nowrap" }}>
                Apply account status rules?
            </Typography>
            <Tooltip
                title={
                    <>
                        Decides what happens to users whose accounts are inactive in the connected directory.
                        <br />
                        <br />
                        • <strong>No (unchanged):</strong> Sync never changes their status here.
                        <br />
                        • <strong>Yes (disable):</strong> Users disabled in Microsoft Entra are disabled here. Not available for Google Workspace.
                        <br />
                        • <strong>Yes (remove):</strong> Inactive users are removed. For Google Workspace, archived users are removed and suspended users are disabled.
                    </>
                }
                placement="top"
            >
                <IconButton size="small">
                    <HelpIcon sx={{ color: "secondary.main" }} />
                </IconButton>
            </Tooltip>
            <Select
                size="small"
                value={action}
                onChange={handleChange}
                disabled={isLoading}
                inputProps={{ "aria-label": "Apply account status rules" }}
                sx={{ minWidth: 160 }}
            >
                {ACTIONS.map((value) => (
                    <MenuItem key={value} value={value} disabled={value === "Disable" && isGoogle}>
                        {ACTION_LABELS[value]}
                        {value === "Disable" && isGoogle ? " — not available for Google" : ""}
                    </MenuItem>
                ))}
            </Select>
            <ConfirmationDialog
                open={pendingAction !== null}
                title={pendingAction ? CONFIRMATIONS[pendingAction].title : ""}
                content={pendingAction ? CONFIRMATIONS[pendingAction].content : ""}
                onConfirm={() => {
                    applyAction(pendingAction);
                    setPendingAction(null);
                }}
                onCancel={() => setPendingAction(null)}
            />
            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={snackbar !== null}
                autoHideDuration={3000}
                onClose={() => setSnackbar(null)}
            >
                <Alert onClose={() => setSnackbar(null)} severity={snackbar?.severity ?? "success"} variant="filled" sx={{ width: "100%" }}>
                    {snackbar?.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

SyncInactiveUserActionControl.propTypes = {
    tenancyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isGoogle: PropTypes.bool
};
